import React from 'react';
import { TileToggle } from '@adjust/dashboard-components';
import { Account, User, UserRole } from '@adjust/dashboard-core/api/types';
import { connectWithStore } from '@common/connectWithStore';
import { selectToggleId, setMainContact, toggleUser, selectLoadingUserId } from '@redux/ducks/users';

import './UserTile.scss';

export interface UserTileOwnProps {
  user: User;
  onPermissions: (userId: number) => void;
  onDetails: (userId: number) => void;
}

interface UserTileStateProps {
  account: Account;
  isOpen: boolean;
  isLoading: boolean;
}

interface UserTileDispatchProps {
  toggleUser: (userId: number) => void;
  setMainContact: (userId: number) => void;
}

type UserTileProps = UserTileOwnProps & UserTileStateProps & UserTileDispatchProps;

export const UserTileBase = ({
  user,
  account,
  isOpen,
  isLoading,
  toggleUser,
  setMainContact,
  onPermissions,
  onDetails,
}: UserTileProps) => {
  const mainContact: boolean = user.id === account.main_contact_id;
  const userRole: string = user.super_admin ? UserRole.SuperAdmin : user.role;
  const canBeMainContact: boolean = !mainContact && userRole === UserRole.Admin;

  return (
    <TileToggle
      extraClasses="user-tile grid-item"
      isLoading={isLoading}
      toggled={isOpen}
      onToggle={() => toggleUser(user.id)}
      front={
        <>
          {mainContact && (
            <div className="main-contact">
              <i className="icon-star-full" />
            </div>
          )}

          <h3 className={`name${!user.full_name ? ' empty' : ''}`} title={user.full_name}>
            {user.full_name || t('label.no-name')}
          </h3>

          <h4 className="email" title={user.email}>
            {user.email}
          </h4>

          <span className={`role-tag role-${user.role}`}>{userRole}</span>
        </>
      }
      back={
        <>
          <h4>{t('label.user-settings')}</h4>
          <ul>
            <li>
              {/* TODO: use here and below UISref for proper href attribute when parent React component is connected to UIRouter */}
              <a onClick={() => onPermissions(user.id)}>
                <i className="icon-access" />
                {t('label.permissions')}
              </a>
            </li>

            <li>
              <a onClick={() => onDetails(user.id)}>
                <i className="icon-user" />
                {t('label.user-details')}
              </a>
            </li>

            {canBeMainContact && (
              <li>
                <a onClick={() => setMainContact(user.id)}>
                  <i className="icon-star-full" />
                  {t('action.set-as-main-contact')}
                </a>
              </li>
            )}
          </ul>
        </>
      }
    />
  );
};

const mapStateToProps = (state, ownProps: UserTileOwnProps): UserTileStateProps => ({
  account: state.global.account,
  isOpen: selectToggleId(state) === ownProps.user.id,
  isLoading: selectLoadingUserId(state) === ownProps.user.id,
});

const mapDispatchToProps: UserTileDispatchProps = {
  setMainContact,
  toggleUser,
};

export const UserTile: React.FC<UserTileOwnProps> = connectWithStore(mapStateToProps, mapDispatchToProps)(UserTileBase);
